/**
=========================================================
* STAIL - Page Header Styles Utility (Glassmorphism Theme)
=========================================================
* Unified dark navy headers for all pages
* Updated: December 2, 2025 - Phase 4A
=========================================================
*/

import brandGradients from 'assets/theme/base/gradients';

/**
 * UNIFIED page header styles - ONE COLOR FOR ALL PAGES.
 * Gold gradient matching Frontpage login button design.
 * 
 * Usage:
 * import { pageHeaderStyles } from 'utils/pageHeaderStyles';
 * <MDBox sx={pageHeaderStyles.primary}>Page Title</MDBox>
 */
export const pageHeaderStyles = {
  // Primary/Default - GOLD GRADIENT (same for ALL pages)
  primary: {
    background: brandGradients.goldGradient,
    color: '#0a1628', // dark navy text for contrast
    padding: '16px 24px',
    borderRadius: '8px',
    marginBottom: '24px',
    boxShadow: '0 4px 12px rgba(244, 197, 66, 0.3)',
    fontWeight: 600,
    fontSize: '1.25rem', // 20px - consistent header size
  },

  // Info/General - SAME GOLD GRADIENT
  info: {
    background: brandGradients.goldGradient,
    color: '#0a1628',
    padding: '16px 24px',
    borderRadius: '8px',
    marginBottom: '24px',
    boxShadow: '0 4px 12px rgba(244, 197, 66, 0.3)',
    fontWeight: 600,
    fontSize: '1.25rem',
  },

  // Success - SAME GOLD GRADIENT (no more green)
  success: {
    background: brandGradients.goldGradient,
    color: '#0a1628',
    padding: '16px 24px',
    borderRadius: '8px',
    marginBottom: '24px',
    boxShadow: '0 4px 12px rgba(244, 197, 66, 0.3)',
    fontWeight: 600,
    fontSize: '1.25rem',
  },

  // Warning - SAME GOLD GRADIENT (no more orange)
  warning: {
    background: brandGradients.goldGradient,
    color: '#0a1628',
    padding: '16px 24px',
    borderRadius: '8px',
    marginBottom: '24px',
    boxShadow: '0 4px 12px rgba(244, 197, 66, 0.3)',
    fontWeight: 600,
    fontSize: '1.25rem',
  },

  // Error - SAME GOLD GRADIENT (no more red)
  error: {
    background: brandGradients.goldGradient,
    color: '#0a1628',
    padding: '16px 24px',
    borderRadius: '8px',
    marginBottom: '24px',
    boxShadow: '0 4px 12px rgba(244, 197, 66, 0.3)',
    fontWeight: 600,
    fontSize: '1.25rem',
  },

  // Special - SAME GOLD GRADIENT
  special: {
    background: brandGradients.goldGradient,
    color: '#0a1628',
    padding: '16px 24px',
    borderRadius: '8px',
    marginBottom: '24px',
    boxShadow: '0 4px 12px rgba(244, 197, 66, 0.3)',
    fontWeight: 600,
    fontSize: '1.25rem',
  },

  // Report - SAME GOLD GRADIENT
  report: {
    background: brandGradients.goldGradient,
    color: '#0a1628',
    padding: '16px 24px',
    borderRadius: '8px',
    marginBottom: '24px',
    boxShadow: '0 4px 12px rgba(244, 197, 66, 0.3)',
    fontWeight: 600,
    fontSize: '1.25rem',
  },

  // Profile - SAME GOLD GRADIENT
  profile: {
    background: brandGradients.goldGradient,
    color: '#0a1628',
    padding: '16px 24px',
    borderRadius: '8px',
    marginBottom: '24px',
    boxShadow: '0 4px 12px rgba(244, 197, 66, 0.3)',
    fontWeight: 600,
    fontSize: '1.25rem',
  },

  // Light - SAME GOLD GRADIENT
  light: {
    background: brandGradients.goldGradient,
    color: '#0a1628',
    padding: '16px 24px',
    borderRadius: '8px',
    marginBottom: '24px',
    boxShadow: '0 4px 12px rgba(244, 197, 66, 0.3)',
    fontWeight: 600,
    fontSize: '1.25rem',
  },

  // Dark - SAME GOLD GRADIENT
  dark: {
    background: brandGradients.goldGradient,
    color: '#0a1628',
    padding: '16px 24px',
    borderRadius: '8px',
    marginBottom: '24px',
    boxShadow: '0 4px 12px rgba(244, 197, 66, 0.3)',
    fontWeight: 600,
    fontSize: '1.25rem',
  },
};

/**
 * Page header size variants for different contexts.
 */
export const pageHeaderSizes = {
  small: {
    padding: '12px 16px',
    fontSize: '1.125rem',
  },
  medium: {
    padding: '16px 24px',
    fontSize: '1.25rem',
  },
  large: {
    padding: '20px 32px',
    fontSize: '1.5rem',
  },
};

/**
 * Combined page header presets for common use cases.
 */
export const pageHeaderPresets = {
  // Main page headers
  mainPage: {
    ...pageHeaderStyles.primary,
    ...pageHeaderSizes.large,
  },

  // Sub-page headers
  subPage: {
    ...pageHeaderStyles.info,
    ...pageHeaderSizes.medium,
  },

  // Customer/Profile pages
  customerPage: {
    ...pageHeaderStyles.special,
    ...pageHeaderSizes.large,
  },

  // Property/Asset pages
  hartaPage: {
    ...pageHeaderStyles.error,
    ...pageHeaderSizes.large,
  },

  // Tenant pages
  penyewaPage: {
    ...pageHeaderStyles.profile,
    ...pageHeaderSizes.large,
  },

  // Report pages
  reportPage: {
    ...pageHeaderStyles.report,
    ...pageHeaderSizes.large,
  },

  // Invoice pages
  inboisPage: {
    ...pageHeaderStyles.success,
    ...pageHeaderSizes.large,
  },

  // Notice pages
  notisPage: {
    ...pageHeaderStyles.warning,
    ...pageHeaderSizes.large,
  },

  // Table header row style (SAME GOLD GRADIENT)
  tableHeader: {
    background: brandGradients.goldGradient,
  },
  
  // Table header success variant (SAME GOLD GRADIENT)
  tableHeaderSuccess: {
    background: brandGradients.goldGradient,
  },
};

/**
 * Search box background style with intermediate gold opacity.
 * Opacity hierarchy:
 * - Page header: 100% (solid gold gradient)
 * - Search box: 15% (this style)
 * - Table header: 8%
 * 
 * Usage:
 * import { searchBoxStyles } from 'utils/pageHeaderStyles';
 * <MDBox sx={searchBoxStyles.container}>Search fields</MDBox>
 */
export const searchBoxStyles = {
  container: {
    background: 'linear-gradient(135deg, rgba(244, 197, 66, 0.15) 0%, rgba(243, 156, 18, 0.15) 100%)',
    px: { xs: 1.5, md: 2 },
    pt: 6,
    pb: 2,
    mt: -7.3,
    borderRadius: 3,
    mb: 2,
    boxShadow: 1,
  },
  // Dropdown/Select field styling to match search box theme
  dropdownField: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "transparent", // Transparent to show search box background
      fontSize: "0.775rem",
      p: 1.2,
    },
    "& .MuiInputLabel-root": {
      fontSize: "0.775rem",
    },
  },
};

export default pageHeaderStyles;
